
import React, { useRef, useEffect, useState } from "react";
import { useGoogleMap } from "@/hooks/useGoogleMap";
import MapLoadingOverlay from "./states/MapLoadingOverlay";
import MapErrorState from "./states/MapErrorState";

interface Location {
  lat: number;
  lng: number;
  name?: string;
}

interface GoogleMapProps {
  initialLocation?: Location | null;
  onLocationSelected: (location: Location) => void;
  onUseFallbackMap?: () => void;
}

const GoogleMap: React.FC<GoogleMapProps> = ({
  initialLocation,
  onLocationSelected,
  onUseFallbackMap
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Use our custom hook
  const { isGoogleMapsLoaded, mapError, map } = useGoogleMap({
    mapRef,
    initialLocation,
    onLocationSelected
  });

  // Add a mousemove event listener to detect when the user interacts with the map
  useEffect(() => {
    if (!mapRef.current) return;
    
    const handleMouseMove = () => {
      if (!hasInteracted) {
        console.log("User interacted with the map");
        setHasInteracted(true);
      }
    };
    
    mapRef.current.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      mapRef.current?.removeEventListener('mousemove', handleMouseMove);
    };
  }, [hasInteracted]);

  // Debug logging
  useEffect(() => {
    console.log("Google Map Component:", {
      isLoaded: isGoogleMapsLoaded,
      hasError: !!mapError,
      mapInstance: !!map,
      initialLocation,
      hasFallbackOption: !!onUseFallbackMap
    });
  }, [isGoogleMapsLoaded, mapError, map, initialLocation, onUseFallbackMap]);

  return (
    <div className="flex-grow relative h-full">
      <div 
        ref={mapRef} 
        className="h-full w-full"
        style={{ cursor: hasInteracted ? 'pointer' : 'default' }}
      >
        {!isGoogleMapsLoaded && !mapError && <MapLoadingOverlay />}
        {mapError && (
          <MapErrorState 
            errorMessage={mapError} 
            onManualLocationSet={(location) => {
              onLocationSelected(location);
            }}
            onUseFallbackMap={onUseFallbackMap}
          />
        )}
      </div>
    </div>
  );
};

export default GoogleMap;
